// have to turn off the following rule to make form onKeyPress preventation working
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import Tooltips from "@flick/front_end_patterns/src/js/dna/tooltips";
import InputLabel from "@flick/front_end_patterns/src/js/forms/form_fields_label";
import FormValidator from "@flick/front_end_patterns/src/js/forms/form_validator";
import classNames from "classnames";
import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  format,
  isValid,
  parse,
} from "date-fns";
import { PRODUCT_LABELS, ROUTES } from "domain/configuration";
import {
  fetchProductDataAction,
  updateFlickSignUpAction,
} from "domain/core/actions";
import { verifyEmail } from "domain/core/api";
import {
  clearState,
  COMMERCIAL_PRODUCTS,
  DEFAULT_PRODUCTS,
  setCurrentFormStep,
  setPromoCode,
  setUserChecks,
  setUserDetails,
  setUserProperty,
} from "domain/core/reducers";
import {
  CommercialProduct,
  Product,
  ProductForDialog,
  ProductLabel,
  SituationAtAddress,
} from "domain/core/types";
import { rollbar } from "lib/api";
import { restartForm } from "lib/hooks";
import { DATE_FORMAT } from "lib/types";
import {
  API_MOBILE_NUMBER_REGEX,
  fetchPromoCookie,
  formatAddress,
  HOME_NUMBER_REGEX,
  sendAnalyticsCustomEvent,
  sendAnalyticsPageEvent,
  sendAnalyticsPageView,
  trimNZMobileNumber,
} from "lib/util";
import debounce from "lodash/debounce";
import qs from "qs";
import React, { useCallback, useEffect, useRef, useState } from "react";
import type { RoutesProps } from "react-router";
import { Link, useLocation, useNavigate } from "react-router";
import { useAppDispatch, useAppSelector } from "store/storeHooks";
import ProductRadioButton from "ui/components/ProductRadioButton";
import { Prompt } from "ui/components/Prompt";

const maxDate = () => addMonths(new Date(), 3);
const minSwitchDate = () => addDays(new Date(), 1);
const minMoveDate = () => addWeeks(new Date(), -2);
const minBirthDate = () => new Date(1900, 0, 1);
const maxBirthDate = () => addYears(new Date(), -18);

const URL_INSERTS: ProductLabel = {
  off_peak: "offpeak",
  flat: "flat",
  // Whole sale is disabled for the time being while spot prices are high
  // freestyle: "wholesale",
  business: "business",
};
interface State {
  situation?: SituationAtAddress;
  medicallyDependent?: boolean;
  estimatedUsageType?: string;
  usageType?: string;
  moveDate?: Date;
  switchDate?: Date;
  smsNotificationsAccepted: boolean;
  creditCheckAccepted: boolean;
  termsAccepted: boolean;
  firstName: string;
  lastName: string;
  preferredName: string;
  companyName: string | undefined;
  mobile: string;
  homeNumber: string;
  email: string;
  birthDate: Date | undefined;
  product: Product | CommercialProduct | undefined;
  errProductTerms: string | undefined;
  errFormIncomplete: string | undefined;
  isCommercialAccount?: boolean;
  availableProducts: Product[] | CommercialProduct[];
  updatingExistingProduct: boolean;
  inputErrors: {
    emailInvalid: boolean;
  };
}

const initialDate = (date: string | undefined) => {
  return date ? parse(date, DATE_FORMAT, new Date()) : undefined;
};

type Props = RoutesProps;

export function Details(props: Props) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    selectedAddress: address,
    user: { details, property, checks: userChecks },
    billEstimates,
    billingEstimateRequestStatus: billEstimateFetchStatus,
    availableProducts,
    estimatedUsageType,
    product: dialogProducts,
    selectedEligibilityType,
    flickUserId,
  } = useAppSelector((store) => store.coreData);

  // Form state handlers
  const [state, setState] = useState<State>({
    situation: property.situation,
    medicallyDependent: property.medicallyDependent,
    usageType: property.usageType,
    moveDate: initialDate(property.moveDate),
    switchDate: initialDate(property.switchDate),
    smsNotificationsAccepted: userChecks.smsSwitchNotificationsAccepted,
    creditCheckAccepted: userChecks.creditCheckAccepted,
    termsAccepted: userChecks.termsAccepted,
    // TODO uncomment when turn back business option
    // isCommercialAccount: userChecks.isCommercialAccount,
    isCommercialAccount: false,
    firstName: details.firstName,
    lastName: details.lastName,
    preferredName: details.prefName,
    // TODO uncomment when turn back business option
    // companyName: details.companyName,
    companyName: undefined,
    email: details.emailAddress,
    mobile: details.mobileNumber,
    homeNumber: details.homeNumber,
    birthDate: initialDate(details.dob),
    product: details.product,
    errProductTerms: undefined,
    errFormIncomplete: undefined,
    availableProducts: availableProducts,
    updatingExistingProduct: false,
    inputErrors: {
      emailInvalid: false,
    },
  });

  // catch all fix for missing address
  useEffect(() => {
    if (flickUserId && !Boolean(address)) {
      restartForm(dispatch, navigate);
    }
  }, [flickUserId]);

  const formRef = useRef(null);

  //@ts-ignore
  const parsedQuery = qs.parse(props?.location?.search, {
    ignoreQueryPrefix: true,
  });
  const emptyProductParams =
    parsedQuery.product === undefined || parsedQuery.product === null;
  const onChangeAddress = () => {
    dispatch(clearState());
    dispatch(setCurrentFormStep(ROUTES.root));
  };

  useEffect(() => {
    const promoCookie = fetchPromoCookie();
    if (promoCookie) {
      dispatch(setPromoCode(promoCookie));
    }
  }, []);

  /*
    Check if any input fields have validation errors
  */
  const hasValidationErrors = () => {
    return Object.values(state.inputErrors).some(Boolean);
  };

  /**
   * Form only submit via Submit button, on both keyboard navigation and mouse click
   */
  const handleFormSubmit = async () => {
    if (state.product === undefined) {
      sendAnalyticsPageEvent(
        "Error",
        "Details Form: Submit Failure",
        "No plan selected",
      );

      setState((currentState) => ({
        ...currentState,
        errProductTerms:
          "We kinda need you to accept our terms and conditions before we can continue",
      }));
    }

    // Prevent form submission if email verification request is in progress
    if (state.inputErrors.emailInvalid === undefined) {
      return;
    }

    if (formRef.current.checkValidity() === false || hasValidationErrors()) {
      setState((currentState) => ({
        ...currentState,
        errFormIncomplete:
          "Looks like we're missing some required information to proceed. Check form fields marked in red above.",
      }));

      return;
    }

    dispatch(
      setUserProperty({
        moveDate: state.moveDate
          ? format(state.moveDate, DATE_FORMAT)
          : undefined,
        switchDate: state.switchDate
          ? format(state.switchDate, DATE_FORMAT)
          : undefined,
        usageType: state.usageType,
        medicallyDependent: state.medicallyDependent,
        situation: state.situation,
      }),
    );

    dispatch(
      setUserDetails({
        dob: state.birthDate ? format(state.birthDate, DATE_FORMAT) : undefined,
        emailAddress: state.email,
        firstName: state.firstName,
        lastName: state.lastName,
        prefName: state.preferredName,
        mobileNumber: trimNZMobileNumber(state.mobile),
        homeNumber: state.homeNumber,
        companyName: state.companyName,
        product: state.product,
      }),
    );

    dispatch(
      setUserChecks({
        marketingAccepted: true,
        smsSwitchNotificationsAccepted: state.smsNotificationsAccepted,
        termsAccepted: state.termsAccepted,
        creditCheckAccepted: state.creditCheckAccepted,
        isCommercialAccount: state.isCommercialAccount,
      }),
    );

    dispatch(updateFlickSignUpAction.request());
    sendAnalyticsCustomEvent("join_details", {
      plan_type: state.product && PRODUCT_LABELS[state.product],
      icp_number: address?.icp,
      address_found: address?.icp ? "yes" : "no",
      eligibility: selectedEligibilityType ? "yes" : "maybe",
      registry_load_type: estimatedUsageType,
      customer_type_system: state.isCommercialAccount
        ? "commercial"
        : "residential",
      self_select_load_group: state.usageType,
    });

    // setting the next form step will also route the user to that form step
    dispatch(setCurrentFormStep(ROUTES.payment));
    navigate(ROUTES.payment);
  };

  /**
   * making sure form validation is called on button submit, both keyboard navigation and click event
   */
  const handleContinuePress = async (
    e: React.KeyboardEvent<HTMLButtonElement>,
  ) => {
    try {
      if (e.key === "Enter") {
        // trigger form default validation
        formRef.current.dispatchEvent(
          new Event("submit", { cancelable: true }),
        );
        await handleFormSubmit();
      }
    } catch (err) {
      // this rollbar error log needs to be monitoring and consider removing in the future
      rollbar.error("Form Signup Step 1 Submit Error", err);
    }
  };

  /**
   * This onKeyDown event is used to prevent the default behavior of the Enter key on the form
   * to prevent premature form validation
   */
  const preventFormSubmitOnEnter = (
    e: React.KeyboardEvent<HTMLFormElement>,
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  const handleRadioButtonChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      setState((currentState) => ({
        ...currentState,
        [name]: value === "true",
      }));
    },
    [],
  );

  const handleFormFieldUpdate = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = event.target;
      setState((currentState) => ({ ...currentState, [name]: value }));
    },
    [],
  );

  /**
   * sets the new value of the email field in the state
   * clears any previous emailInvalid error
   * calls the debounced email verification function
   */
  const handleEmailFieldUpdate = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = event.target;
      setState((currentState) => ({
        ...currentState,
        [name]: value,
        inputErrors: { ...currentState.inputErrors, emailInvalid: undefined },
      }));

      handleEmailVerification(event);
    },
    [],
  );

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, checked } = event.target;
      setState((currentState) => ({ ...currentState, [name]: checked }));
    },
    [],
  );

  const handleProductChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const product = event.target.value as Product;
      const updatingExistingProduct =
        !emptyProductParams && parsedQuery.product !== product;

      setState((currentState) => ({
        ...currentState,
        product,
        errProductTerms: undefined,
        updatingExistingProduct,
      }));
    },
    [parsedQuery.product, emptyProductParams],
  );

  const handleDateChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      const date = new Date(value);
      if (name === "switchDate")
        setState((currentState) => ({
          ...currentState,
          [name]: date as Date,
          moveDate: undefined,
        }));
      if (name === "moveDate")
        setState((currentState) => ({
          ...currentState,
          [name]: date as Date,
          switchDate: undefined,
        }));

      setState((currentState) => ({ ...currentState, [name]: date }));
    },
    [],
  );

  /**
   * Debounced function to handle email verification.
   * Check the input's validity, and call AddressFinder service to `verifyEmail` if valid.
   * Update state based on the verification result.
   */

  const handleEmailVerification = useCallback(
    debounce(async (event) => {
      const { value } = event.target;
      if (event.target.checkValidity()) {
        const response = await verifyEmail(value);

        setState((currentState) => ({
          ...currentState,
          inputErrors: {
            ...currentState.inputErrors,
            emailInvalid: !response.is_verified,
          },
        }));
      }
    }, 500),
    [],
  );

  const writeFirstNameToPreferredName = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (state.preferredName) {
        return;
      }
      event.target.name = "preferredName";
      handleFormFieldUpdate(event);
      // reset the event target name so that the first name gets updated to
      event.target.name = "firstName";
    },
    [state.preferredName, handleFormFieldUpdate],
  );

  const isOnlyAvailableProduct = availableProducts.length === 1;

  useEffect(() => {
    const icpClassification =
      billEstimates?.data?.[0]?.attributes.location_classification;

    const filteredAvailableProducts = (): Product[] | CommercialProduct[] => {
      // If the business sign up radio buttons are unselected
      if (state.isCommercialAccount === undefined) return availableProducts;
      // If the ICP is commercial
      if (icpClassification === "commercial") {
        // and the user is registering a business
        if (state.isCommercialAccount) return availableProducts;

        // and the user is registering a residence
        return DEFAULT_PRODUCTS; // flat or off-peak
      }
      // If the ICP is unknown or residential
      else {
        // but the user is registering a business
        if (state.isCommercialAccount) return COMMERCIAL_PRODUCTS; // business

        // and the user is registering a residence
        return availableProducts;
        // Else if the ICP is commerical
      }
    };

    // Only check radio button for commercial customers
    const updatedProduct = filteredAvailableProducts().includes("business")
      ? "business"
      : state.product;

    setState((currentState) => ({
      ...currentState,
      product: updatedProduct,
      availableProducts: filteredAvailableProducts(),
    }));
  }, [
    isOnlyAvailableProduct,
    availableProducts,
    billEstimates,
    state.isCommercialAccount,
    state.product,
  ]);

  const lowUserType = estimatedUsageType === "low";
  const query = `query {
    planCollection {
      items {
        name
        title
        slug
        pricingDetailsContentCollection {
          items {
            __typename
            ... on QuestionAndAnswer {
              question {
                json
              }
              answer {
                json
              }
            }
            ... on RichTextBlock {
              content {
                json
              }
            }
          }
        }
      }
    }
  }`;

  useEffect(() => {
    sendAnalyticsPageView(ROUTES.details);

    window.addEventListener("beforeunload", throwAlert);
    dispatch(fetchProductDataAction.request({ query: JSON.stringify(query) }));
    return () => {
      window.removeEventListener("beforeunload", throwAlert);
    };
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const throwAlert = (e: any) => {
    e.preventDefault();
    e.returnValue = "";
  };

  useEffect(() => {
    new InputLabel().start();
    new FormValidator().start();
    new Tooltips().start();
    if (!emptyProductParams) {
      const product = parsedQuery.product as Product;
      setState((currentState) => ({ ...currentState, product }));
    }
  }, [parsedQuery.product, emptyProductParams]);

  const dateFormatter = (date: Date | undefined) => {
    if (!date) return "";
    if (isValid(date)) {
      return format(date, DATE_FORMAT);
    }
  };
  const location = useLocation();

  return (
    <section className="layout page">
      <Prompt
        when={!location.pathname.startsWith("/join")}
        message={"Leave the page? Changes you have made may not be saved."}
      />
      <ul className="page_indicator_wrapper">
        <li className="page_indicator page_indicator_active">01</li>
        <li className="page_indicator">02</li>
      </ul>
      <h1 className="heading heading_page">Sign up details</h1>
      <p>
        Tū meke! Flick is available at your place. Once you’ve sent us your
        address and contact details, we’ll send you through an email with
        everything you need. After confirming your details we’ll get busy with
        setting up your connection.
      </p>
      <h2 className="heading heading_subpage heading_subpage--compact">
        Your address
      </h2>
      <p>
        {address && <>{formatAddress(address)} &nbsp;&nbsp;&nbsp;</>}
        <Link
          className="button button_secondary"
          onClick={onChangeAddress}
          to={ROUTES.root}
          discover="none"
        >
          Change address
        </Link>
      </p>
      <form
        data-validate
        data-synthetic-submit
        onSubmit={handleFormSubmit}
        onKeyDown={preventFormSubmitOnEnter}
        ref={formRef}
      >
        <fieldset className="form_group form_row no_bottom_margin">
          <legend>What's the situation at your address?</legend>
          <div className="radio_confirm form_field">
            <input
              type="radio"
              name="situation"
              value="moved_in"
              id="situation_moved_in"
              checked={state.situation === "moved_in"}
              onChange={handleFormFieldUpdate}
              data-msg-value-missing="Please select the situation at your address"
              required
            />
            <label htmlFor="situation_moved_in">
              <span>I'm moving in, or I've just moved in.</span>
            </label>
            {state.situation === "moved_in" && (
              <div className="form_subform">
                <div className="form_field">
                  <div>
                    <input
                      type="date"
                      name="moveDate"
                      id="move_in_date"
                      className="form_field_small input_active"
                      min={format(minMoveDate(), DATE_FORMAT)}
                      max={format(maxDate(), DATE_FORMAT)}
                      value={dateFormatter(state.moveDate)}
                      onChange={handleDateChange}
                      data-msg-range-overflow="That's way too far in the future for our systems."
                      data-msg-range-underflow="We can’t go that far back, sadly."
                      data-msg-value-missing="When would you like to be connected with Flick? You can back-date this too."
                      required
                    />
                    <label htmlFor="move_in_date">Move in date</label>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="radio_confirm form_field">
            <input
              type="radio"
              name="situation"
              id="situation_switch"
              value="switch_provider"
              checked={state.situation === "switch_provider"}
              onChange={handleFormFieldUpdate}
              data-msg-value-missing="Please select the situation at your address"
              required
            />
            <label htmlFor="situation_switch">
              <span>
                I already have a power company here and want to change to Flick
              </span>
            </label>
            {state.situation === "switch_provider" && (
              <div className="form_subform">
                <div className="form_field">
                  <div>
                    <input
                      type="date"
                      name="switchDate"
                      id="switch_provider_date"
                      className="form_field_small input_active"
                      min={format(minSwitchDate(), DATE_FORMAT)}
                      max={format(maxDate(), DATE_FORMAT)}
                      value={dateFormatter(state.switchDate)}
                      onChange={handleDateChange}
                      data-msg-range-overflow="That's way too far in the future for our systems."
                      data-msg-range-underflow="We can’t go that far back, sadly."
                      data-msg-value-missing="When would you like to make the switch?"
                      required
                    />
                    <label htmlFor="switch_provider_date">Switch date</label>
                  </div>
                </div>
              </div>
            )}
          </div>
        </fieldset>
        <fieldset className="form_group form_row no_bottom_margin">
          <legend>
            Is there someone at this address who is medically dependant on
            electricity?
            <span
              data-tooltip="A 'medically dependent customer' relies on electricity for critical medical support for example, an oxygen machine. If you or anyone living with you at your home is dependent on electricity for medical reasons, it is important for you to contact us to enable us to provide you with the right level of support."
              className="tooltip_inline"
            ></span>
          </legend>
          <ul className="form_field_collection">
            <li>
              <input
                type="radio"
                name="medicallyDependent"
                value="true"
                id="medically_dependant_true"
                checked={state.medicallyDependent === true}
                onChange={handleRadioButtonChange}
                data-msg-value-missing="Please confirm whether there is someone who is medically dependant on electricity at your address"
                required
              />
              <label htmlFor="medically_dependant_true">
                <span>Yes</span>
              </label>
            </li>
            <li>
              <input
                type="radio"
                name="medicallyDependent"
                value="false"
                id="medically_dependant_false"
                checked={state.medicallyDependent === false}
                onChange={handleRadioButtonChange}
                data-msg-value-missing="Please confirm whether there is someone who is medically dependant on electricity at your address"
                required
              />
              <label htmlFor="medically_dependant_false">
                <span>No</span>
              </label>
            </li>
          </ul>
        </fieldset>
        {/* <fieldset className="radio_confirm form_group form_row">
          <legend>Are you signing up as a business?</legend>
          <ul className="form_field_collection">
            <li>
              <input
                type="radio"
                name="isCommercialAccount"
                value="true"
                id="is_commerical_account_true"
                data-testid="is_commerical_account_true"
                checked={state.isCommercialAccount === true}
                onChange={handleRadioButtonChange}
                data-msg-value-missing="Please confirm whether you are signing up as a business"
                required
              />
              <label htmlFor="is_commerical_account_true">
                <span>
                  Yes
                  <span className="a11y">
                    {" "}
                    I am registering a business address
                  </span>
                </span>
              </label>
            </li>
            <li>
              <input
                type="radio"
                name="isCommercialAccount"
                value="false"
                id="is_commerical_account_false"
                checked={state.isCommercialAccount === false}
                onChange={handleRadioButtonChange}
                data-msg-value-missing="Please confirm whether you are signing up as a business"
                required
              />
              <label htmlFor="is_commerical_account_false">
                <span>
                  No{" "}
                  <span className="a11y">
                    {" "}
                    I am registering a residential address
                  </span>
                </span>
              </label>
            </li>
          </ul>
        </fieldset> */}
        <fieldset
          className="form_group form_row"
          data-testid="flickProductFieldset"
        >
          <h2 className="heading heading_subpage">Your plan</h2>
          {billEstimateFetchStatus !== "Pending" && (
            <ul className="form_field_collection">
              {(
                state.availableProducts as Array<Product | CommercialProduct>
              ).map((product) => {
                const dialogProduct = dialogProducts?.filter(
                  (dialog: ProductForDialog) => dialog.slug === product,
                );
                return (
                  // only render a radio button if we have a product name for it
                  PRODUCT_LABELS[product] && (
                    <ProductRadioButton
                      key={product}
                      checked={state.product === product}
                      currentProduct={product}
                      handleProductChange={handleProductChange}
                      billEstimates={billEstimates}
                      region={address?.region}
                      dialogProduct={dialogProduct}
                    />
                  )
                );
              })}
            </ul>
          )}
          <br />
          {state.isCommercialAccount !== true && (
            <p className="h5 no_bottom_margin">
              Ka pai! Both the Off Peak and Flat plans include our Flickin’ Best
              Plan Promise.
            </p>
          )}
          {state.updatingExistingProduct && (
            <p className="form_field_helper">
              Your plan option has been updated. Please click on the Pricing &
              plans details link to review full info.
            </p>
          )}
        </fieldset>
        <fieldset className="form_group form_row">
          <h2 className="heading heading_subpage">About you</h2>
          <div className="form_row form_row_inline">
            <div className="form_field">
              <div>
                <input
                  id="first_name"
                  name="firstName"
                  type="text"
                  value={state.firstName}
                  onChange={handleFormFieldUpdate}
                  onBlur={writeFirstNameToPreferredName}
                  data-msg-value-missing="What’s your given name?"
                  required
                />
                <label htmlFor="first_name">First name</label>
              </div>
            </div>

            <div className="form_field">
              <div>
                <input
                  id="last_name"
                  name="lastName"
                  type="text"
                  value={state.lastName}
                  onChange={handleFormFieldUpdate}
                  data-msg-value-missing="Please enter your surname or family name."
                  required
                />
                <label htmlFor="last_name">Last name</label>
              </div>
            </div>
          </div>

          <div className="form_row form_row_inline">
            <div className="form_field">
              <div>
                <input
                  id="preferred_name"
                  name="preferredName"
                  type="text"
                  className={state.preferredName ? "input_active" : ""}
                  value={state.preferredName}
                  onChange={handleFormFieldUpdate}
                  data-msg-value-missing="Maybe we could use your first name?"
                  required
                />
                <label htmlFor="preferred_name">Preferred name</label>
              </div>
              <span className="form_field_helper">
                This is how we will greet you
              </span>
            </div>

            <div className="form_field">
              <div>
                <input
                  type="date"
                  name="birthDate"
                  id="birth_date"
                  min={format(minBirthDate(), DATE_FORMAT)}
                  max={format(maxBirthDate(), DATE_FORMAT)}
                  value={dateFormatter(state.birthDate)}
                  onChange={handleDateChange}
                  data-msg-range-overflow="You need to be 18 or older to sign up for a power account."
                  data-msg-range-underflow="That's way too far in the past for our systems."
                  data-msg-value-missing="When's your birthday?"
                  required
                />
                <label htmlFor="birth_date">Date of birth</label>
              </div>
              <span className="form_field_helper">
                We use this for credit checking purposes
              </span>
            </div>
          </div>
          {/* TODO uncomment when turn back business option */}
          {/* <div className="form_row">
            <div className="form_field">
              <div>
                <input
                  type="text"
                  id="company_name"
                  name="companyName"
                  value={state.companyName}
                  onChange={handleFormFieldUpdate}
                  maxLength={50}
                  required={state.isCommercialAccount}
                />
                <label htmlFor="company_name">
                  Business name {!state.isCommercialAccount && `(optional)`}
                </label>
              </div>
              <span className="form_field_helper">
                If signing up as a business this can be used for billing
                purposes
              </span>
            </div>
          </div> */}
        </fieldset>

        <fieldset className="form_group form_row">
          <h2 className="heading heading_subpage">Contact details</h2>
          <div className="form_row form_row_inline">
            <div
              className={classNames("form_field", {
                form_field_error: state.inputErrors.emailInvalid,
              })}
            >
              <div>
                <input
                  type="email"
                  name="email"
                  id="email"
                  value={state.email}
                  onChange={handleEmailFieldUpdate}
                  data-msg-value-missing="This will be our default way of contacting you"
                  data-msg-pattern-mismatch="E-mail addresses typically need an '@' symbol, please"
                  required
                />
                <label htmlFor="email">Email address</label>
              </div>
              <span className="form_field_helper">
                We may contact you to help complete your application
              </span>
              {state.inputErrors.emailInvalid && (
                <span className="form_field_helper validation_error">
                  Invalid email
                </span>
              )}
            </div>

            <div className="form_field">
              <div>
                <input
                  id="mobile_number"
                  type="text"
                  name="mobile"
                  value={state.mobile}
                  pattern={API_MOBILE_NUMBER_REGEX}
                  onChange={handleFormFieldUpdate}
                  data-msg-value-missing="We’ll need this to be able to get in touch"
                  data-msg-pattern-mismatch="Please enter a valid mobile number"
                  required
                />
                <label htmlFor="mobile_number">Mobile number</label>
              </div>
            </div>
          </div>

          <div className="form_row form_row_inline">
            <div className="form_field">
              <div>
                <input
                  type="text"
                  id="home_number"
                  name="homeNumber"
                  value={state.homeNumber}
                  pattern={HOME_NUMBER_REGEX}
                  onChange={handleFormFieldUpdate}
                  data-msg-pattern-mismatch="Please enter a valid phone number"
                />
                <label htmlFor="home_number">Home number (optional)</label>
              </div>
              <span className="form_field_helper">
                Please input numbers without spaces and start with your country
                code (eg. +64).
              </span>
            </div>
            <div className="form_field"></div>
          </div>
          <div className="form_row">
            <legend>Update me on my application via... (optional)</legend>
            <div className="checkbox_confirm">
              <input
                type="checkbox"
                name="smsNotificationsAccepted"
                id="sms_switch_notification_accepted"
                checked={state.smsNotificationsAccepted}
                onChange={handleCheckboxChange}
              />
              <label htmlFor="sms_switch_notification_accepted">
                <span>Text message</span>
              </label>
            </div>
          </div>
        </fieldset>

        <h2 className="heading heading_subpage">Your typical power use</h2>
        <fieldset className="form_group form_row">
          <legend>
            What type of power user are you?
            <span
              data-tooltip={`Your meter indicates a ${estimatedUsageType} user at this property. Typically ${estimatedUsageType} users spend ${
                lowUserType ? "under" : "over"
              } $215 a month on power bills using ${
                lowUserType ? "less" : "more"
              } than 8,000-9,000 kWh per year, depending on your region.`}
              className="tooltip_inline"
            ></span>
          </legend>
          <ul className="form_field_collection">
            <li>
              <input
                type="radio"
                name="usageType"
                value="low"
                id="usage_type_low"
                checked={state.usageType === "low"}
                onChange={handleFormFieldUpdate}
                data-msg-value-missing="Please confirm how much you spend on power per month"
                required={state.usageType === undefined}
              />
              <label htmlFor="usage_type_low">
                <span>Low user (typically spend less than $215 per month)</span>
              </label>
            </li>
            <li>
              <input
                type="radio"
                name="usageType"
                value="standard"
                id="usage_type_standard"
                checked={state.usageType === "standard"}
                onChange={handleFormFieldUpdate}
                data-msg-value-missing="Please confirm how much you spend on power per month"
                required={state.usageType === undefined}
              />
              <label htmlFor="usage_type_standard">
                <span>
                  Standard user (typically spend more than $215 per month)
                </span>
              </label>
            </li>
          </ul>
        </fieldset>

        <fieldset className="form_group form_row no_bottom_margin">
          <div className="checkbox_confirm">
            <input
              type="checkbox"
              name="creditCheckAccepted"
              checked={state.creditCheckAccepted}
              onChange={handleCheckboxChange}
              id="checkbox_credit_check"
              required
            />
            <label htmlFor="checkbox_credit_check">
              <span>I'm OK with you credit-checking me</span>
            </label>
          </div>
        </fieldset>

        <fieldset className="form_group form_row no_top_margin no_bottom_margin">
          <div className="checkbox_confirm">
            <input
              type="checkbox"
              disabled={!state.product}
              name="termsAccepted"
              checked={state.termsAccepted}
              onChange={handleCheckboxChange}
              id="terms_credit_check"
              required
            />
            <label htmlFor="terms_credit_check">
              <span>
                I accept the&nbsp;
                <a
                  href="https://www.flickelectric.co.nz/terms-and-conditions"
                  className="link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms &amp; Conditions
                </a>
                &nbsp;of being a Flick customer, and I accept the&nbsp;
                {state.product ? (
                  <a
                    href={`https://www.flickelectric.co.nz/${
                      URL_INSERTS[state.product]
                    }-terms-and-conditions`}
                    className="link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {PRODUCT_LABELS[state.product]} Terms &amp; Conditions
                  </a>
                ) : (
                  <span>plan Terms &amp; Conditions.</span>
                )}
              </span>
            </label>
            <span className="form_field_helper validation_error">
              {state.errProductTerms}
            </span>
          </div>
        </fieldset>

        <div className="form_row">
          <div className="form_actions form_field space-between">
            <button
              type="submit"
              className="button button_primary"
              id="button-next-step-details"
              onKeyDown={handleContinuePress}
            >
              Continue
            </button>
            <Link
              className="button button_secondary"
              onClick={onChangeAddress}
              to={ROUTES.root}
              discover="none"
            >
              Back
            </Link>
          </div>
        </div>
        {(state.inputErrors.emailInvalid === undefined ||
          state.errFormIncomplete) && (
          <span className="form_field_helper validation_error">
            {state.inputErrors.emailInvalid === undefined
              ? "Looks like we're still validating your email, please wait for validation to be complete"
              : state.errFormIncomplete}
          </span>
        )}
      </form>
    </section>
  );
}
